import classNames from 'classnames';
import { DisplayTime } from 'common/display-state/display-time.js';
import { HTMLRenderer } from 'common/html-renderer.js';
import { IconWithText } from 'common/icon/icon-with-text.js';
import { IconNameV1 } from 'common/icon/iconV1.js';
import { useSelector } from 'react-redux';
import { IRepoState } from 'redux-state/rootReducer.js';
import { ILocationWarningType, IWarning } from 'redux-state/weather-warnings/types.js';
import {
  WarningSeverity,
  findHighestSeverityLevel,
  getSeverityInfo,
} from 'utilities/weather-warnings/warning-severity.js';
import { getLoadedLocationWarnings } from 'utilities/weather-warnings/warnings.js';

interface IProps {
  location: ILocationWarningType;
  title?: Element | string;
  setWarningSeverity?: (warningSeverity: WarningSeverity) => void;
}

/**
 * WarningHighSeas is a component that takes a location object and will then search for that location's warnings
 * in the warnings domain.
 *
 * This is designed to replace the need to wrap your entire module with the LocationContext consumer
 *
 * This component is designed for use in the high seas route, this could be renamed and made generic
 *
 * A property in the JSON would look like this
 * ```
 * [
 *   {
 *     "type": "module",
 *     "name": "myModuleName",
 *
 *     Other props...
 *
 *    "location": {
 *       "type": "high-seas",
 *       "key": "pacific"
 *     }
 *   }
 * ]
 *
 */
export const HighSeasWarnings = ({ title, location, setWarningSeverity }: IProps) => {
  const warnings = GetSevereWeatherHook({ location, setWarningSeverity });
  if (!warnings?.length) {
    return null;
  }

  return (
    <>
      {title || <h2 className="u-mT-lg">Warnings in force</h2>}
      <div className="u-mT-sm u-mB-sm" data-card-name="warning-high-seas">
        {warnings.map((warning: IWarning, idx: number) => (
          <HighSeasWarning warning={warning} key={idx} />
        ))}
      </div>
    </>
  );
};

const HighSeasWarning = ({ warning }: { warning: IWarning }) => {
  const { icon, warnLevel, areaDescription, issuedAt, text, impact, name } = warning;

  const { iconColor, textColorClass } = getSeverityInfo(warnLevel);

  return (
    <div className="u-mT-sm u-mB-sm">
      <IconWithText icon={`${String(icon)}-${iconColor}` as IconNameV1} classes="IconWithText--vCenter">
        <p className={classNames('u-textSemibold u-mT-0 u-mB-0', textColorClass)}>{name}</p>
      </IconWithText>

      {areaDescription && (
        <p className="u-textBold">
          <span>{areaDescription}</span>
        </p>
      )}

      {text && (
        <div>
          <HTMLRenderer content={text} />
        </div>
      )}

      {impact && <p>{impact}</p>}

      {issuedAt && (
        <p className="u-textGrey u-mT-sm u-mB-0">
          <span className="u-textBold">Issued: </span>
          <DisplayTime date={issuedAt} />
        </p>
      )}
    </div>
  );
};

const GetSevereWeatherHook = ({ location, setWarningSeverity }: IProps) => {
  const warningsState = useSelector((state: IRepoState) => state.WEATHER_WARNINGS);
  if (!location || !warningsState || !warningsState.warnings) {
    return [];
  }

  const warningState = getLoadedLocationWarnings(location, warningsState.warnings);
  if (warningState) {
    const warnings = warningState.warnings;
    if (setWarningSeverity) {
      const highestWarningSeverity = findHighestSeverityLevel((warnings ?? []).map((warning) => warning.warnLevel));
      setWarningSeverity(highestWarningSeverity);
    }
    return warnings;
  }

  return [];
};
